import {
  useVaultPermissions,
  useVaultInvitations,
  useCreateInvitation,
  useRevokePermission,
  useRevokeInvitation,
  useResendInvitation,
  useUpdateFolderPermission,
} from '@/hooks/useVault';
import {
  FolderVaultPermission,
  InvitationStatus,
  PermissionRole,
  VaultPermission,
} from '@/types/vault.types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit3,
  Loader2,
  Mail,
  MailPlus,
  RefreshCw,
  Save,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface FolderMembersTabProps {
  vaultId: string;
}

const PERMISSION_KEYS: (keyof FolderVaultPermission)[] = [
  'canViewDocuments',
  'canReadContent',
  'canUploadDocument',
  'canSendToEmployeeVault',
  'canViewEmployeeVaults',
  'canDepositToEmployeeVault',
];

const inviteSchema = z.object({
  email: z.string().email('Email invalide'),
  message: z.string().max(500).optional(),
  canViewDocuments: z.boolean().default(true),
  canReadContent: z.boolean().default(false),
  canUploadDocument: z.boolean().default(false),
  canSendToEmployeeVault: z.boolean().default(false),
  canViewEmployeeVaults: z.boolean().default(false),
  canDepositToEmployeeVault: z.boolean().default(false),
});

type InviteFormData = z.infer<typeof inviteSchema>;

export function FolderMembersTab({ vaultId }: FolderMembersTabProps) {
  const { t } = useTranslation();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState<string | null>(
    null
  );
  const [expandedInvitations, setExpandedInvitations] = useState(false);

  const { data: permissions, isLoading: isLoadingPermissions } =
    useVaultPermissions(vaultId);
  const { data: invitations } = useVaultInvitations(vaultId);
  const revokePermission = useRevokePermission(vaultId);

  const pendingInvitations =
    invitations?.filter(inv => inv.status === InvitationStatus.PENDING) || [];
  const otherInvitations =
    invitations?.filter(inv => inv.status !== InvitationStatus.PENDING) || [];

  if (isLoadingPermissions) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  const handleRevoke = (permissionId: string) => {
    if (
      window.confirm(
        t(
          'vault.members.revokeConfirm',
          "Retirer l'accès de ce membre au coffre-fort ?"
        )
      )
    ) {
      revokePermission.mutate(permissionId);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
            <Users className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              {t('vault.members.title', 'Membres du coffre-fort')}
            </h3>
            <p className="text-sm text-neutral-500">
              {t(
                'vault.members.description',
                'Gérez les accès et permissions des membres'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInviteModal(true)}
            className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            {t('vault.members.invite', 'Inviter')}
          </button>
        </div>
      </div>

      {/* Current Members */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-neutral-700">
          {t('vault.members.currentMembers', 'Membres actuels')}
          {permissions && (
            <span className="ml-2 text-neutral-400">
              ({permissions.length})
            </span>
          )}
        </h4>

        {permissions && permissions.length > 0 ? (
          <div className="space-y-2">
            {permissions.map(perm => (
              <MemberCard
                key={perm.uuid}
                permission={perm}
                isEditing={editingPermission === perm.uuid}
                onStartEdit={() => setEditingPermission(perm.uuid)}
                onCancelEdit={() => setEditingPermission(null)}
                onRevoke={() => handleRevoke(perm.uuid)}
                vaultId={vaultId}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-200 bg-neutral-50 p-8 text-center">
            <Shield className="mb-3 h-10 w-10 text-neutral-300" />
            <p className="text-sm text-neutral-600">
              {t('vault.members.noMembers', 'Aucun membre pour le moment')}
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-primary-600 hover:bg-primary-700 mt-3 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              {t('vault.members.inviteFirst', 'Inviter le premier membre')}
            </button>
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-neutral-700">
            {t('vault.members.pendingInvitations', 'Invitations en attente')}
            <span className="ml-2 text-amber-600">
              ({pendingInvitations.length})
            </span>
          </h4>
          <div className="space-y-2">
            {pendingInvitations.map(inv => (
              <InvitationCard
                key={inv.uuid}
                invitation={inv}
                vaultId={vaultId}
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Invitations */}
      {otherInvitations.length > 0 && (
        <div className="space-y-3">
          <button
            onClick={() => setExpandedInvitations(!expandedInvitations)}
            className="flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-neutral-700"
          >
            {expandedInvitations ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {t('vault.members.pastInvitations', 'Invitations passées')}
            <span className="text-neutral-400">
              ({otherInvitations.length})
            </span>
          </button>
          {expandedInvitations && (
            <div className="space-y-2">
              {otherInvitations.map(inv => (
                <InvitationCard
                  key={inv.uuid}
                  invitation={inv}
                  vaultId={vaultId}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteFolderMemberModal
          vaultId={vaultId}
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
        />
      )}
    </div>
  );
}

// ===== Member Card =====

interface MemberCardProps {
  permission: VaultPermission;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onRevoke: () => void;
  vaultId: string;
}

function MemberCard({
  permission,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onRevoke,
  vaultId,
}: MemberCardProps) {
  const { t } = useTranslation();
  const updatePermission = useUpdateFolderPermission(vaultId);
  const [editValues, setEditValues] = useState<Partial<FolderVaultPermission>>(
    {}
  );

  const startEdit = () => {
    setEditValues({
      canViewDocuments: permission.canViewDocuments ?? false,
      canReadContent: permission.canReadContent ?? false,
      canUploadDocument: permission.canUploadDocument ?? false,
      canSendToEmployeeVault: permission.canSendToEmployeeVault ?? false,
      canViewEmployeeVaults: permission.canViewEmployeeVaults ?? false,
      canDepositToEmployeeVault: permission.canDepositToEmployeeVault ?? false,
    });
    onStartEdit();
  };

  const handleSave = () => {
    updatePermission.mutate(
      { permissionId: permission.uuid, data: editValues },
      { onSuccess: () => onCancelEdit() }
    );
  };

  const permissionLabels = getPermissionLabels(t);
  const activePermissions = PERMISSION_KEYS.filter(key => permission[key]);

  const displayName = permission.user
    ? `${permission.user.firstName || ''} ${permission.user.lastName || ''}`.trim() ||
      permission.user.email
    : t('common.unknown', 'Inconnu');

  const initial =
    permission.user?.firstName?.charAt(0) ||
    permission.user?.email?.charAt(0)?.toUpperCase() ||
    '?';

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
            {initial}
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900">
              {displayName}
            </p>
            {permission.user?.email &&
              displayName !== permission.user.email && (
                <p className="text-xs text-neutral-500">
                  {permission.user.email}
                </p>
              )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {!isEditing ? (
            <>
              <button
                onClick={startEdit}
                className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                title={t('common.edit', 'Modifier')}
              >
                <Edit3 className="h-4 w-4" />
              </button>
              <button
                onClick={onRevoke}
                className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600"
                title={t('vault.members.removeAccess', "Retirer l'accès")}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={updatePermission.isPending}
                className="bg-primary-600 hover:bg-primary-700 flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors disabled:opacity-50"
              >
                {updatePermission.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                {t('common.save', 'Enregistrer')}
              </button>
              <button
                onClick={onCancelEdit}
                className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Permissions display / edit */}
      <div className="mt-3">
        {isEditing ? (
          <div className="grid grid-cols-2 gap-2">
            {PERMISSION_KEYS.map(key => (
              <label
                key={key}
                className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 transition-colors hover:bg-neutral-50"
              >
                <input
                  type="checkbox"
                  checked={editValues[key] ?? false}
                  onChange={e =>
                    setEditValues(prev => ({
                      ...prev,
                      [key]: e.target.checked,
                    }))
                  }
                  className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-neutral-300"
                />
                <span className="text-xs text-neutral-700">
                  {permissionLabels[key]}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {activePermissions.length > 0 ? (
              activePermissions.map(key => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                >
                  <CheckCircle className="h-3 w-3" />
                  {permissionLabels[key]}
                </span>
              ))
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                <ShieldCheck className="h-3 w-3" />
                {t('vault.members.fullAccess', 'Acces complet')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Invitation Card =====

interface InvitationCardProps {
  invitation: {
    uuid: string;
    email: string;
    status: InvitationStatus;
    createdAt: string;
    expiresAt: string;
    grantedPermissions?: FolderVaultPermission | null;
  };
  vaultId: string;
}

function InvitationCard({ invitation, vaultId }: InvitationCardProps) {
  const { t } = useTranslation();
  const revokeInvitation = useRevokeInvitation(vaultId);
  const resendInvitation = useResendInvitation(vaultId);
  const permissionLabels = getPermissionLabels(t);

  const isPending = invitation.status === InvitationStatus.PENDING;
  const isExpired = invitation.status === InvitationStatus.EXPIRED;

  const statusConfig = {
    [InvitationStatus.PENDING]: {
      icon: Clock,
      className: 'bg-amber-100 text-amber-700',
      label: t('vault.invitations.status.pending', 'En attente'),
    },
    [InvitationStatus.ACCEPTED]: {
      icon: CheckCircle,
      className: 'bg-success-100 text-success-700',
      label: t('vault.invitations.status.accepted', 'Acceptee'),
    },
    [InvitationStatus.EXPIRED]: {
      icon: XCircle,
      className: 'bg-neutral-100 text-neutral-600',
      label: t('vault.invitations.status.expired', 'Expiree'),
    },
    [InvitationStatus.REVOKED]: {
      icon: XCircle,
      className: 'bg-red-100 text-red-700',
      label: t('vault.invitations.status.revoked', 'Revoquee'),
    },
    [InvitationStatus.DECLINED]: {
      icon: XCircle,
      className: 'bg-red-100 text-red-700',
      label: t('vault.invitations.status.declined', 'Refusee'),
    },
    [InvitationStatus.CANCELLED]: {
      icon: XCircle,
      className: 'bg-neutral-100 text-neutral-600',
      label: t('vault.invitations.status.cancelled', 'Annulee'),
    },
  };

  const status =
    statusConfig[invitation.status] || statusConfig[InvitationStatus.PENDING];
  const StatusIcon = status.icon;

  const grantedKeys = invitation.grantedPermissions
    ? PERMISSION_KEYS.filter(key => invitation.grantedPermissions?.[key])
    : [];

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200 text-xs font-medium text-neutral-600">
            {invitation.email.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900">
              {invitation.email}
            </p>
            <div className="mt-0.5 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}
              >
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </span>
              <span className="text-xs text-neutral-400">
                {new Date(invitation.createdAt).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {(isPending || isExpired) && (
            <button
              onClick={() =>
                resendInvitation.mutate({ invitationId: invitation.uuid })
              }
              disabled={resendInvitation.isPending}
              className="flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50"
            >
              {resendInvitation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              {t('vault.invitations.resend', 'Renvoyer')}
            </button>
          )}
          {isPending && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    t(
                      'vault.invitations.revokeConfirm',
                      'Revoquer cette invitation ?'
                    )
                  )
                ) {
                  revokeInvitation.mutate(invitation.uuid);
                }
              }}
              disabled={revokeInvitation.isPending}
              className="rounded-lg p-1.5 text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Granted permissions preview */}
      {grantedKeys.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {grantedKeys.map(key => (
            <span
              key={key}
              className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-xs text-neutral-600"
            >
              {permissionLabels[key]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Invite Modal =====

interface InviteFolderMemberModalProps {
  vaultId: string;
  isOpen: boolean;
  onClose: () => void;
}

function InviteFolderMemberModal({
  vaultId,
  isOpen,
  onClose,
}: InviteFolderMemberModalProps) {
  const { t } = useTranslation();
  const createInvitation = useCreateInvitation(vaultId);
  const permissionLabels = getPermissionLabels(t);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      canViewDocuments: true,
      canReadContent: false,
      canUploadDocument: false,
      canSendToEmployeeVault: false,
      canViewEmployeeVaults: false,
      canDepositToEmployeeVault: false,
    },
  });

  const onSubmit = async (data: InviteFormData) => {
    try {
      await createInvitation.mutateAsync({
        email: data.email,
        permissionRole: PermissionRole.CREATOR,
        message: data.message,
        grantedPermissions: {
          canViewDocuments: data.canViewDocuments,
          canReadContent: data.canReadContent,
          canUploadDocument: data.canUploadDocument,
          canSendToEmployeeVault: data.canSendToEmployeeVault,
          canViewEmployeeVaults: data.canViewEmployeeVaults,
          canDepositToEmployeeVault: data.canDepositToEmployeeVault,
        },
      });
      reset();
      onClose();
    } catch {
      // Error handled by hook
    }
  };

  if (!isOpen) return null;

  const permissionDescriptions = getPermissionDescriptions(t);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-6">
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
              <MailPlus className="h-5 w-5 text-indigo-600" />
            </div>
            <h2 className="text-lg font-bold text-neutral-900">
              {t('vault.members.inviteTitle', 'Inviter un membre')}
            </h2>
          </div>
          <p className="text-sm text-neutral-500">
            {t(
              'vault.members.inviteDescription',
              "L'utilisateur recevra un email d'invitation pour acceder au coffre-fort."
            )}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              {t('vault.members.emailLabel', 'Adresse email')}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                {...register('email')}
                type="email"
                className="focus:border-primary-500 focus:ring-primary-100 block w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-10 text-sm shadow-sm transition-colors focus:ring-2 focus:outline-none"
                placeholder="membre@exemple.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Permissions */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.members.permissionsLabel', 'Permissions')}
            </label>
            <div className="space-y-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              {PERMISSION_KEYS.map(key => (
                <Controller
                  key={key}
                  name={key}
                  control={control}
                  render={({ field }) => (
                    <label className="flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors hover:bg-white">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="text-primary-600 focus:ring-primary-500 mt-0.5 h-4 w-4 rounded border-neutral-300"
                      />
                      <div>
                        <span className="text-sm font-medium text-neutral-800">
                          {permissionLabels[key]}
                        </span>
                        <p className="text-xs text-neutral-500">
                          {permissionDescriptions[key]}
                        </p>
                      </div>
                    </label>
                  )}
                />
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">
              {t(
                'vault.members.messageLabel',
                'Message personnalise (optionnel)'
              )}
            </label>
            <textarea
              {...register('message')}
              rows={2}
              className="focus:border-primary-500 focus:ring-primary-100 block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm shadow-sm transition-colors focus:ring-2 focus:outline-none"
              placeholder={t(
                'vault.members.messagePlaceholder',
                'Ajoutez un message pour le destinataire...'
              )}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary-600 hover:bg-primary-700 flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {isSubmitting
                ? t('common.sending', 'Envoi...')
                : t('vault.members.sendInvitation', "Envoyer l'invitation")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===== Permission Label Helpers =====

function getPermissionLabels(
  t: (key: string, fallback: string) => string
): Record<keyof FolderVaultPermission, string> {
  return {
    canViewDocuments: t(
      'vault.permissions.canViewDocuments',
      'Voir les documents'
    ),
    canReadContent: t('vault.permissions.canReadContent', 'Lire le contenu'),
    canUploadDocument: t(
      'vault.permissions.canUploadDocument',
      'Deposer un document'
    ),
    canSendToEmployeeVault: t(
      'vault.permissions.canSendToEmployeeVault',
      'Envoyer vers coffre employe'
    ),
    canViewEmployeeVaults: t(
      'vault.permissions.canViewEmployeeVaults',
      'Voir les coffres employes'
    ),
    canDepositToEmployeeVault: t(
      'vault.permissions.canDepositToEmployeeVault',
      'Deposer dans coffre employe'
    ),
  };
}

function getPermissionDescriptions(
  t: (key: string, fallback: string) => string
): Record<keyof FolderVaultPermission, string> {
  return {
    canViewDocuments: t(
      'vault.permissions.canViewDocumentsDesc',
      'Voir la liste des documents du coffre-fort'
    ),
    canReadContent: t(
      'vault.permissions.canReadContentDesc',
      'Telecharger et lire le contenu des documents'
    ),
    canUploadDocument: t(
      'vault.permissions.canUploadDocumentDesc',
      'Ajouter de nouveaux documents dans le coffre'
    ),
    canSendToEmployeeVault: t(
      'vault.permissions.canSendToEmployeeVaultDesc',
      "Transmettre des documents vers les coffres d'employes"
    ),
    canViewEmployeeVaults: t(
      'vault.permissions.canViewEmployeeVaultsDesc',
      "Voir la liste des coffres-forts d'employes rattaches"
    ),
    canDepositToEmployeeVault: t(
      'vault.permissions.canDepositToEmployeeVaultDesc',
      "Deposer des documents directement dans les coffres d'employes"
    ),
  };
}
